import type { Combo, OS, ShortcutEventLike } from './types';

export interface ParsedCombo {
  mod: boolean;
  ctrl: boolean;
  meta: boolean;
  alt: boolean;
  shift: boolean;
  key: string;
}

const KEY_ALIASES: Record<string, string> = {
  esc: 'Escape',
  escape: 'Escape',
  enter: 'Enter',
  return: 'Enter',
  space: ' ',
  backspace: 'Backspace',
  delete: 'Delete',
  tab: 'Tab',
  up: 'ArrowUp',
  down: 'ArrowDown',
  left: 'ArrowLeft',
  right: 'ArrowRight',
};

export function parseCombo(combo: Combo): ParsedCombo {
  const parts = combo.split('+');
  // A literal "+" key is written as "Mod++"
  const keyPart = combo.endsWith('++') ? '+' : (parts.pop() ?? '');
  const mods = combo.endsWith('++') ? parts.slice(0, -2) : parts;
  const p: ParsedCombo = { mod: false, ctrl: false, meta: false, alt: false, shift: false, key: KEY_ALIASES[keyPart.toLowerCase()] ?? keyPart };
  for (const m of mods) {
    const l = m.toLowerCase();
    if (l === 'mod') p.mod = true;
    else if (l === 'ctrl' || l === 'control') p.ctrl = true;
    else if (l === 'meta' || l === 'cmd') p.meta = true;
    else if (l === 'alt' || l === 'option') p.alt = true;
    else if (l === 'shift') p.shift = true;
    else throw new Error(`Unknown modifier "${m}" in combo "${combo}"`);
  }
  return p;
}

/** The physical key code a combo key corresponds to (used when Alt/Shift change event.key). */
function codeFor(key: string): string | null {
  if (/^[a-z]$/i.test(key)) return `Key${key.toUpperCase()}`;
  if (/^[0-9]$/.test(key)) return `Digit${key}`;
  const map: Record<string, string> = { '[': 'BracketLeft', ']': 'BracketRight', '\\': 'Backslash', '/': 'Slash', '.': 'Period', ',': 'Comma', ';': 'Semicolon', "'": 'Quote', '-': 'Minus', '=': 'Equal', '`': 'Backquote' };
  return map[key] ?? null;
}

export function isApple(os: OS): boolean {
  return os === 'mac' || os === 'ios';
}

export function matchesCombo(e: ShortcutEventLike, combo: Combo, os: OS): boolean {
  const p = parseCombo(combo);
  const apple = isApple(os);
  const wantMeta = p.meta || (p.mod && apple);
  const wantCtrl = p.ctrl || (p.mod && !apple);
  if (e.metaKey !== wantMeta || e.ctrlKey !== wantCtrl || e.altKey !== p.alt || e.shiftKey !== p.shift) return false;
  const code = codeFor(p.key);
  if (code && e.code === code) return true;
  return e.key.toLowerCase() === p.key.toLowerCase();
}

const APPLE_SYMBOLS: Record<string, string> = { Enter: '↵', Backspace: '⌫', Delete: '⌦', Escape: 'Esc', ArrowUp: '↑', ArrowDown: '↓', ArrowLeft: '←', ArrowRight: '→', Tab: '⇥', ' ': 'Space' };

export function formatCombo(combo: Combo, os: OS): string {
  const p = parseCombo(combo);
  const key = p.key.length === 1 ? p.key.toUpperCase() : p.key;
  if (isApple(os)) {
    let s = '';
    if (p.ctrl) s += '⌃';
    if (p.alt) s += '⌥';
    if (p.shift) s += '⇧';
    if (p.mod || p.meta) s += '⌘';
    return s + (APPLE_SYMBOLS[p.key] ?? key);
  }
  const parts: string[] = [];
  if (p.mod || p.ctrl) parts.push('Ctrl');
  if (p.meta) parts.push('Win');
  if (p.alt) parts.push('Alt');
  if (p.shift) parts.push('Shift');
  parts.push(p.key === ' ' ? 'Space' : key);
  return parts.join('+');
}

export function detectOS(userAgent: string, platform = ''): OS {
  // navigator.platform reflects the real OS even when the UA string is generic.
  if (/iPhone|iPad|iPod/i.test(platform) || /iPhone|iPad|iPod/i.test(userAgent)) return 'ios';
  if (/Android/i.test(userAgent)) return 'android';
  if (platform) {
    if (/Mac/i.test(platform)) return 'mac';
    if (/Win/i.test(platform)) return 'windows';
    if (/Linux|X11/i.test(platform)) return 'linux';
  }
  if (/Mac/i.test(userAgent)) return 'mac';
  if (/Win/i.test(userAgent)) return 'windows';
  return 'linux';
}
